/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Box, Button, Typography } from "@mui/material";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Lock, X } from "lucide-react";
import LockIcon from "@mui/icons-material/Lock";
import CloseIcon from "@mui/icons-material/Close";
import { Loader } from "../../common/loader";
import AlertDialog from "../../common/AlertDialog";
import {
  ROLES,
  TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS,
} from "../../helpers/constants";
import SecurePaymentPanel from "../../common/SecurePaymentPanel";
import { Button as TailwindButton } from "../../common/customDesignedComponents/button";
import { userSigupAction } from "../../../redux/slices/user/userSignUpSlice";
import { userProfileAction } from "../../../redux/slices/user/userProfileSlice";
import {
  buildAdminPropertyCreatePayloadFromStored,
  clearStoredPromoCode,
  decryptPayload,
  getObjLength,
  getStoredPromoCode,
  persistStoredPromoCode,
  reactPixelEventOnlyForCustomer,
  resolveEnhancedMonitoringReportIds,
} from "../../helpers/utils";
import { mapCreateUserApiUrl } from "../../for-service-provider/createUserApiPaths";
import { rememberCarInEscrowPinSkip } from "../../for-service-provider/carPortfolioStatus";
import { propertiesAction } from "../../../redux/slices/properties/propertiesSlice";
import { attachEnhancedToCompleteUpgradeFlags } from "../../helpers/generatePropertySecurityQuestions";
import { clientPaymentCardTarget, savedCardOutcome } from "./savedCardSelection";

const CardPayment = ({
  onChangeModalScreen,
  answers,
  planId,
  onCloseModalHandler,
  isByPassOptionAvailable,
  selectedData,
  addPromoCode = false,
  from = "",
  callListingAPI = () => {},
  propertiesIds = [],
  ids = [],
  uid = null,
  default_card_id = null,
  tabComponent = false,
  skipVerification = false,
  isUserCreateProperty = false,
  handleCloseAddPropertyModal,
  isAssistant = false,
  selectedCategory = "complete_protection",
  isAdmin = false,
  isUpgradeCategory = false,
  userEmail = undefined,
  isTailwindComponent = false,
  isServiceProvider = false,
  isEnhancedToCompleteProtection = false,
  ownershipVerificationAlreadyComplete = false,
  promoCode = undefined,
  isAdminCreateUser = false,
  finalAmount = null,
  onFinalAmountChange = () => {},
  // Inside a checkout that already draws the secure bar and close button.
  hideSecureHeader = false,
}) => {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const inlineCheckout = from === "add-property";
  // states
  const [cardData, setCardData] = useState({});
  const [cardElementComplete, setCardElementComplete] = useState(false);
  const [alertMsg, setAlertMsg] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [valdiateModal, setValdiateModal] = useState(false);
  const [promoCodeValue, setPromoCodeValue] = useState(
    () => getStoredPromoCode() || promoCode || undefined,
  );
  const appliedPromoCodeRef = useRef(
    getStoredPromoCode() || promoCode || null,
  );
  const [isPaymentDone, setIsPaymentDone] = useState(false);
  const [stripeErr, setStripeErr] = useState("");
  const [pendingPayAfterCard, setPendingPayAfterCard] = useState(false);

  // store data
  const {
    cardDetails,
    cardLoader,
    userProfileDetails,
    loggedInEmail,
    addNewCard,
    addNewCardLoader,
  } = useSelector((state) => state.userProfile);
  const {
    paymentLoader,
    propertyPayment,
    propertyQuestion,
    selectedProperty,
    genearteQuesPayload,
  } = useSelector((state) => state?.properties);
  const { mailingAddress, statesCodeList, property_ids, propertyIds } =
    useSelector((state) => state.signUpUser);

  const { investorReportResultData, invToken } = useSelector(
    (state) => state?.propertyRiskManagement,
  );

  // useEffect(() => {
  //   if (Object.keys(propertyPayment).length > 0) {
  //     paymentResponse();
  //   }
  // }, [propertyPayment]);

  // After admin card creation via admin/users/{uid}/card, trigger payment with the new card ID
  // (admin-only path; SP client create uses service-provider/customer/users/.../card)
  useEffect(() => {
    if (!pendingPayAfterCard || !getObjLength(addNewCard)) {
      return;
    }

    const outcome = savedCardOutcome(addNewCard);
    if (outcome.kind === "saved") {
      setPendingPayAfterCard(false);
      paySecure({ card_id: outcome.id });
      return;
    }
    if (outcome.kind === "saved-no-id" || outcome.kind === "duplicate") {
      setPendingPayAfterCard(false);
      callListingAPI();
      setStripeErr(
        outcome.kind === "duplicate"
          ? "This card is already saved. Select it and press Pay."
          : "",
      );
      return;
    }

    setPendingPayAfterCard(false);
    setStripeErr(
      outcome.message || "Unable to save card. Please try again.",
    );
  }, [addNewCard, pendingPayAfterCard]); // eslint-disable-line react-hooks/exhaustive-deps

  const metaData = () => {
    const plan_details = localStorage.getItem("planDetails");
    let metadata = {};
    if (plan_details) {
      metadata = {
        property_id: selectedProperty?.property_id,
        property_address: selectedProperty?.address,
        email: loggedInEmail,
        ...JSON.parse(plan_details),
      };
    } else {
      metadata = {
        property_id: selectedProperty?.id,
        property_address: selectedProperty?.id,
        email: loggedInEmail,
      };
    }
    console.log("plan_details==>", metadata);
    return metadata;
  };

  const paymentResponse = () => {
    setAlertMsg(true);
    // fb metadata

    let metadata = metaData();
    if (propertyPayment?.success) {
      rememberCarInEscrowPinSkip();
      localStorage.removeItem("car_portfolio_id");
      localStorage.removeItem("carEnrollment");
      localStorage.removeItem("carPortfolioPrefill");
      if (
        decryptPayload(localStorage.getItem("roles"))?.includes(
          ROLES.customer,
        ) &&
        !propertyPayment?.data?.actionRequired
      ) {
        reactPixelEventOnlyForCustomer("Property Added by User", metadata);
      }
      setSuccessMsg(propertyPayment?.message);
      setErrMsg("");
    } else {
      setSuccessMsg("");
      setErrMsg(
        propertyPayment?.data?.message ||
          propertyPayment?.message ||
          propertyPayment?.error ||
          "Payment failed. Please try again or contact support.",
      );
      if (
        decryptPayload(localStorage.getItem("roles"))?.includes(
          ROLES.customer,
        ) &&
        !propertyPayment?.data?.actionRequired
      ) {
        reactPixelEventOnlyForCustomer("Add Property failed", metadata);
      }
    }
  };

  const resolveBillingName = (...candidates) => {
    for (const value of candidates) {
      const trimmed = (value || "").trim();
      if (trimmed) {
        return trimmed;
      }
    }
    return "";
  };

  const buildBillingFields = () => ({
    first_name: resolveBillingName(
      userProfileDetails?.data?.first_name,
      mailingAddress?.data?.first_name,
    ),
    last_name: resolveBillingName(
      userProfileDetails?.data?.last_name,
      mailingAddress?.data?.last_name,
    ),
    address:
      mailingAddress?.data?.address?.address ||
      userProfileDetails?.data?.address ||
      selectedProperty?.address ||
      "",
    city:
      mailingAddress?.data?.address?.city ||
      userProfileDetails?.data?.city ||
      selectedProperty?.city ||
      "",
    state:
      mailingAddress?.data?.address?.state ||
      userProfileDetails?.data?.state ||
      selectedProperty?.state ||
      "",
    zip:
      mailingAddress?.data?.address?.zip ||
      userProfileDetails?.data?.zip ||
      selectedProperty?.zip ||
      "",
  });

  // The owner removed the promo on the payment screen: don't fall back to a
  // default code (promoCode prop, saved promo) the shown total no longer has.
  const promoRemovedRef = useRef(false);

  const handlePromoCodeValueChange = useCallback((code) => {
    const trimmed = String(code || "").trim();
    if (trimmed) {
      promoRemovedRef.current = false;
      persistStoredPromoCode(trimmed);
      appliedPromoCodeRef.current = trimmed;
      setPromoCodeValue(trimmed);
      return;
    }
    promoRemovedRef.current = true;
    clearStoredPromoCode();
    appliedPromoCodeRef.current = null;
    setPromoCodeValue(undefined);
  }, []);

  const resolveAppliedPromoCode = useCallback(() => {
    if (
      localStorage.getItem("car_portfolio_id") ||
      localStorage.getItem("carEnrollment")
    ) {
      return null;
    }
    if (appliedPromoCodeRef.current) {
      return appliedPromoCodeRef.current;
    }
    if (promoRemovedRef.current) {
      return null;
    }
    if (promoCodeValue) {
      return promoCodeValue;
    }
    const fromStorage = getStoredPromoCode();
    if (fromStorage) {
      return fromStorage;
    }
    if (finalAmount?.promo_code) {
      return finalAmount.promo_code;
    }
    // Admin Add Property: only use an explicit promo prop (e.g. resume),
    // never the owner's stored default promocode.
    if (from === "add-property") {
      return promoCode || null;
    }
    if (promoCode) {
      return promoCode;
    }
    if (selectedData?.owner?.[0]?.promocode_name) {
      return selectedData.owner[0].promocode_name;
    }
    return null;
  }, [finalAmount, from, promoCode, promoCodeValue, selectedData?.owner]);

  const paySecure = (overrides = {}) => {
    const listedCards = Array.isArray(cardDetails?.data) ? cardDetails.data : [];
    let activeCreditId = listedCards.filter((i) => i.default === 1);
    let selectedCardData = listedCards.filter(
      (i) => i.id === parseInt(activeCreditId[0]?.id),
    );
    console.log(activeCreditId, "selcted card data==>", selectedCardData);

    let cardId;
    if (overrides.card_id) {
      cardId = overrides.card_id;
    } else if (cardData.length > 0) {
      cardId = cardData?.[0]?.id;
    } else {
      cardId = selectedCardData?.[0]?.id;
    }

    const appliedPromoCode = resolveAppliedPromoCode();

    let inputParams = {
      ...answers,
      ...buildBillingFields(),
      plan_id: planId,
      verified:
        ownershipVerificationAlreadyComplete ||
        // Basic/Enhanced list rows always report is_verified, so a skipped
        // upgrade must not treat that row flag as ownership verification.
        (!skipVerification &&
          (selectedProperty?.is_verified === 1 ||
            selectedProperty?.is_verified === true)) ||
        propertyQuestion?.data?.is_verified,
      skip_verification: skipVerification,
    };
    if (cardId) {
      inputParams.card_id = cardId;
    }
    if (overrides.token) {
      inputParams.token = overrides.token;
      delete inputParams.card_id;
    }
    if (getObjLength(selectedData)) {
      inputParams = { ...selectedData, ...inputParams };
    }
    // Always attach the payment-screen promo last so it is not dropped by
    // selectedData / genearteQuesPayload spreads.
    if (appliedPromoCode) {
      inputParams.promo_code = appliedPromoCode;
    }

    const upgradeFlagSources = {
      property: selectedProperty || selectedData,
      propertyQuestion,
      forceUpgrade: Boolean(isEnhancedToCompleteProtection),
    };
    inputParams = attachEnhancedToCompleteUpgradeFlags(
      inputParams,
      upgradeFlagSources,
    );
    console.log(propertiesIds, "input params==>", inputParams);

    // Promo + Enhanced→CP upgrade flags last so spreads cannot drop them.
    const withPromo = (data) => {
      let next = appliedPromoCode
        ? { ...data, promo_code: appliedPromoCode }
        : data;
      return attachEnhancedToCompleteUpgradeFlags(next, upgradeFlagSources);
    };

    const resolvePostPaymentPropertyIds = () => {
      // Prefer Owner Verification staged ids (set on Next), then props, then
      // the currently selected property as a last resort.
      const fromProps = Array.isArray(propertiesIds)
        ? propertiesIds.filter(Boolean)
        : [];
      const fromSignup = Array.isArray(propertyIds)
        ? propertyIds.filter(Boolean)
        : [];
      if (tabComponent && fromProps.length > 0) {
        return fromProps;
      }
      if (fromSignup.length > 0) {
        return fromSignup;
      }
      if (fromProps.length > 0) {
        return fromProps;
      }
      return [
        selectedData?.property_id ||
          selectedProperty?.property_id ||
          selectedData?.id ||
          selectedProperty?.id,
      ].filter(Boolean);
    };

    // SP personal (/servicer-properties) Complete Protection: use SP
    // customer-verify-answers. Do not use post-payment / subscription-payment
    // here (those stay for SP client and other SP personal payment flows).
    const isSpPersonalCompleteProtectionPayment =
      isServiceProvider &&
      !isAdminCreateUser &&
      from === "add-property" &&
      selectedCategory === "complete_protection";

    if (isSpPersonalCompleteProtectionPayment) {
      dispatch(
        propertiesAction.paymentForProperty({
          url: "service-provider/customer-verify-answers",
          data: withPromo({ ...inputParams, ...genearteQuesPayload }),
          token: true,
        }),
      );
      return;
    }

    // Admin Add Property / Add Subscription, or SP client enrollment checkout.
    // Never use admin post-payment for customer self-serve (/properties) —
    // `(isAdmin || !isAssistant)` previously routed customers to
    // admin/users/{id}/post-payment whenever from="add-property" (401).
    const shouldUseAdminPostPayment =
      Boolean(uid) &&
      !isUserCreateProperty &&
      ((Boolean(isAdmin) &&
        (from === "add-subscriptions" || from === "add-property")) ||
        (isServiceProvider && Boolean(isAdminCreateUser)));
    if (shouldUseAdminPostPayment) {
      let storedCar = {};
      try {
        storedCar = JSON.parse(localStorage.getItem("carEnrollment") || "{}");
      } catch {
        storedCar = {};
      }
      let data = {
        plan: planId,
        user_id: uid,
        ...buildBillingFields(),
        step: 4,
        step_key: "PAYMENT",
        from_admin: isAdmin,
        property_ids: resolvePostPaymentPropertyIds(),
        ...(storedCar.payment_plan ? { payment_plan: storedCar.payment_plan } : {}),
        ...(storedCar.revenue_share_choice
          ? { revenue_share_choice: storedCar.revenue_share_choice }
          : {}),
        ...(storedCar.car_agent_property_id
          ? { car_agent_property_id: storedCar.car_agent_property_id }
          : {}),
        ...(Array.isArray(storedCar.car_agent_property_ids)
          ? { car_agent_property_ids: storedCar.car_agent_property_ids }
          : {}),
      };
      if (cardId) {
        data.card_id = cardId;
      }
      if (overrides.token) {
        data.token = overrides.token;
        delete data.card_id;
      }
      if (
        from !== "add-property" &&
        selectedData?.owner?.[0]?.county_user_id &&
        selectedData?.owner?.[0]?.promocode_name
      ) {
        data.promo_code = selectedData?.owner?.[0]?.promocode_name;
      }
      if (appliedPromoCode) {
        data.promo_code = appliedPromoCode;
      }
      console.log("== Paymnet done...", data);
      // SP personal (/servicer-properties): subscription-payment.
      // SP client (/clients/*) keeps service-provider/customer/users/{id}/post-payment.
      let paymentURL =
        isServiceProvider && !isAdminCreateUser
          ? "service-provider/subscription-payment"
          : mapCreateUserApiUrl(
              `admin/users/${uid}/post-payment`,
              isServiceProvider,
            );
      if (selectedCategory === "enhanced_monitoring") {
        const propertyForCategory = selectedProperty || selectedData;
        const propertyCategory =
          propertyForCategory?.category ?? propertyForCategory?.product_tier;
        const isCompleteProtectionToEnhanced =
          from === "add-subscriptions" &&
          propertyCategory === "complete_protection";

        paymentURL = isCompleteProtectionToEnhanced
          ? "admin/add-enhanced-monitoring-property"
          : isServiceProvider && uid
            ? `service-provider/customer/users/${uid}/payment-enhanced-monitoring`
            : "payment-enhanced-monitoring";

        if (isCompleteProtectionToEnhanced) {
          const paymentFields = {
            plan: data.plan,
            card_id: data.card_id,
            token: data.token,
            promo_code: data.promo_code,
            property_ids: data.property_ids,
            first_name: data.first_name,
            last_name: data.last_name,
            address: data.address,
            city: data.city,
            state: data.state,
            zip: data.zip,
            from_admin: data.from_admin,
            skip_verification: skipVerification || undefined,
          };
          data = {
            ...buildAdminPropertyCreatePayloadFromStored(
              propertyForCategory,
              uid,
              {
                statesCodeList,
                extra: {
                  step: 2,
                  step_key: "GET_MAILING_ADDRESS",
                },
              },
            ),
            ...Object.fromEntries(
              Object.entries(paymentFields).filter(
                ([, value]) => value != null,
              ),
            ),
          };
        } else if (isUpgradeCategory) {
          data.report_id = selectedProperty?.id;
          data.property_id = selectedProperty?.property_id;
        } else {
          const enhancedReportIds = resolveEnhancedMonitoringReportIds({
            property_ids,
            propertyIds,
          });
          data.report_id =
            enhancedReportIds.length > 0
              ? enhancedReportIds
              : propertyQuestion?.data?.risk_report_id ||
                propertyQuestion?.data?.report_id ||
                propertyQuestion?.data?.id ||
                selectedData?.id;
        }

        if (userProfileDetails?.data?.role?.includes(ROLES?.customer)) {
          data.user_id = userProfileDetails?.data?.user_id;
        }
      }
      if (
        skipVerification &&
        paymentURL !== "admin/add-enhanced-monitoring-property"
      ) {
        data.skip_verification = true;
      }
      dispatch(
        propertiesAction.paymentForProperty({
          url: paymentURL,
          data: withPromo(data),
          token: true,
        }),
      );
    } else {
      if (
        decryptPayload(localStorage.getItem("roles"))?.includes(
          ROLES.serviceProvider,
        )
      ) {
        dispatch(
          propertiesAction.paymentForProperty({
            url: "service-provider/customer-verify-answers",
            data: withPromo({ ...inputParams, ...genearteQuesPayload }),
            token: true,
          }),
        );
      } else if (
        userProfileDetails?.data?.user_type?.includes(ROLES?.professional) &&
        !userProfileDetails?.data?.role?.includes(ROLES?.customer) &&
        !isAssistant
      ) {
        dispatch(
          propertiesAction.paymentForProperty({
            url: "professional/professional-verify-answers",
            data: withPromo({ ...inputParams, ...genearteQuesPayload }),
            token: true,
          }),
        );
      } else {
        if (invToken && !isAssistant) {
          const payload = {
            user_id: investorReportResultData?.data?.user?.id,
            card_id: inputParams?.card_id,
            plan: inputParams?.plan_id,
            property_id: propertiesIds,
            payment_from_investor_page: 1,
          };
          dispatch(
            propertiesAction.paymentForProperty({
              url: "investor-payment",
              data: withPromo(payload),
              token: true,
            }),
          );
        } else {
          let url = "customer/customer-verify-answers";
          if (selectedCategory === "enhanced_monitoring") {
            const existingReportId =
              propertyQuestion?.data?.id ||
              selectedData?.id ||
              selectedProperty?.id;
            if (isUpgradeCategory || existingReportId) {
              url =
                isServiceProvider && uid
                  ? `service-provider/customer/users/${uid}/payment-enhanced-monitoring`
                  : "payment-enhanced-monitoring";
              inputParams.report_id = isUpgradeCategory
                ? selectedProperty?.id
                : existingReportId;
              if (isUpgradeCategory) {
                inputParams.property_id = selectedProperty?.property_id;
              }
            } else {
              url = "customer/add-enhanced-monitoring-property";
            }
            if (userProfileDetails?.data?.role?.includes(ROLES?.customer)) {
              inputParams.user_id = userProfileDetails?.data?.user_id;
            }
          }
          if (isAssistant) {
            url = "assistant/customer-verify-answers";
          }
          dispatch(
            propertiesAction.paymentForProperty({
              url,
              data: withPromo({ ...inputParams, ...genearteQuesPayload }),
              token: !isAssistant,
              brokertoken: isAssistant,
            }),
          );
        }
      }
    }
  };

  const validateStripeErr = (event) => {
    setStripeErr("");
    if (event?.complete !== undefined) {
      setCardElementComplete(Boolean(event.complete));
    }
  };

  const handleCardDataChange = useCallback((cards) => {
    if (!cards?.[0]?.id) {
      setCardElementComplete(false);
    }
    setCardData(cards);
  }, []);

  const handlePayClick = async () => {
    if (cardData?.[0]?.id) {
      paySecure();
      return;
    }

    if (!inlineCheckout || !stripe || !elements) {
      paySecure();
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setStripeErr("Enter your card details to continue.");
      return;
    }

    const result = await stripe.createToken(cardElement);
    if (result.error) {
      setStripeErr(result.error.message);
      return;
    }

    setStripeErr("");

    // True admin (not SP): create card on the client, then pay with returned card id.
    // SP personal must never hit admin/users/.../card.
    if (isAdmin && uid && !isServiceProvider) {
      setPendingPayAfterCard(true);
      dispatch(
        userProfileAction.addNewCard({
          url: `admin/users/${uid}/card`,
          data: { token: result.token.id },
          token: true,
        }),
      );
      return;
    }

    // SP paying for a client: client-scoped card create
    if (isServiceProvider && isAdminCreateUser && uid) {
      setPendingPayAfterCard(true);
      dispatch(
        userProfileAction.addNewCard({
          url: `service-provider/customer/users/${uid}/card`,
          data: { token: result.token.id },
          token: true,
        }),
      );
      return;
    }

    paySecure({ token: result.token.id });
  };

  const goback = () => {
    dispatch(userSigupAction.fetchTotalPaymentDetails({}));
    const storedPlan =
      localStorage.getItem("add_subscription_category") ||
      decryptPayload(localStorage.getItem("category"));
    const resolvedPlan = selectedCategory || storedPlan;
    const fromAddSubscriptionPicker =
      Boolean(isUserCreateProperty) ||
      userProfileDetails?.data?.role?.includes(ROLES?.customer);

    // Enhanced / Basic were chosen on Add Subscription — return there.
    if (
      fromAddSubscriptionPicker &&
      resolvedPlan !== "complete_protection"
    ) {
      onChangeModalScreen("categoryWisePricing");
      return;
    }

    if (fromAddSubscriptionPicker && resolvedPlan === "complete_protection") {
      if (ownershipVerificationAlreadyComplete || skipVerification) {
        handleCloseAddPropertyModal?.();
        return;
      }
      onChangeModalScreen("questions");
      return;
    }

    onChangeModalScreen("subscriptions");
  };

  const onSuccessFun = () => {
    localStorage.removeItem("promoCode");
    localStorage.removeItem("referralCode");
    clearStoredPromoCode();
    appliedPromoCodeRef.current = null;
    setPromoCodeValue(undefined);
    if (
      decryptPayload(localStorage.getItem("roles"))?.includes(ROLES.customer)
    ) {
      reactPixelEventOnlyForCustomer(
        "Property Added by User with 3d secure card",
        metaData(),
      );
    }
    onCloseModalHandler("success");
    dispatch(propertiesAction.setAddPropertySubscription({}));
  };
  const onFailedFun = () => {
    if (
      decryptPayload(localStorage.getItem("roles"))?.includes(ROLES.customer)
    ) {
      reactPixelEventOnlyForCustomer("Add Property failed", metaData());
    }
    dispatch(propertiesAction.setAddPropertySubscription({}));
    onCloseModalHandler("error");
  };

  useEffect(() => {
    if (getObjLength(propertyPayment)) {
      paymentResponse();
      if (getObjLength(propertyPayment?.data)) {
        setValdiateModal(true);
        setIsPaymentDone(propertyPayment?.data?.payment_done);
      }
      if (propertyPayment?.success) {
        if (propertyPayment?.data?.actionRequired) {
          setValdiateModal(false);
          return;
        }
        if (
          !propertyPayment?.data?.actionRequired &&
          propertyPayment?.data?.length === 0
        ) {
          setValdiateModal(true);
          return;
        }
      } else {
        setValdiateModal(true);
      }
    }
  }, [propertyPayment]);

  const isPayDisabled =
    (!cardData?.[0]?.id && !cardElementComplete) || paymentLoader;
  const clientCardTarget = clientPaymentCardTarget({
    isServiceProvider,
    isAdminCreateUser,
    uid,
    invToken,
  });

  const paymentPanel = (
    <>
      {from === "add-subscriptions" || from === "add-property" ? (
        <SecurePaymentPanel
          displayLabel={false}
          cardData={handleCardDataChange}
          paymentData={propertyPayment}
          onSuccessFun={onSuccessFun}
          onFailedFun={onFailedFun}
          userId={uid}
          from={from}
          callListingAPI={callListingAPI}
          addPromoCode={addPromoCode}
          promoCodeValue={handlePromoCodeValueChange}
          paySecure={paySecure}
          uid={uid}
          default_card_id={default_card_id}
          isUserCreateProperty={isUserCreateProperty}
          selectedData={selectedData}
          selectedCategory={selectedCategory}
          isAdmin={isAdmin}
          userEmail={userEmail}
          isTailwindComponent={isTailwindComponent}
          inlineCheckout={inlineCheckout}
          stripeErr={stripeErr}
          validateStripeErr={validateStripeErr}
          onInlineCardPay={handlePayClick}
          inlinePayLoading={paymentLoader}
          isServiceProvider={isServiceProvider}
          isAdminCreateUser={isAdminCreateUser}
          isUpgradeCategory={isUpgradeCategory}
          isEnhancedToCompleteProtection={isEnhancedToCompleteProtection}
          finalAmount={finalAmount}
          onFinalAmountChange={onFinalAmountChange}
        />
      ) : (
        <SecurePaymentPanel
          displayLabel={false}
          cardData={handleCardDataChange}
          paymentData={propertyPayment}
          onSuccessFun={onSuccessFun}
          onFailedFun={onFailedFun}
          userId={answers?.user_id || uid}
          addPromoCode={addPromoCode}
          promoCodeValue={handlePromoCodeValueChange}
          // County Investor page: show the county default code it priced with.
          defaultPromoCode={invToken ? promoCode : undefined}
          paySecure={paySecure}
          uid={clientCardTarget.uid}
          default_card_id={default_card_id}
          isUserCreateProperty={isUserCreateProperty}
          selectedData={selectedData}
          propertiesIds={invToken ? propertiesIds : undefined}
          callListingAPI={
            clientCardTarget.refreshSavedCards ? callListingAPI : undefined
          }
          selectedCategory={selectedCategory}
          userEmail={userEmail}
          isTailwindComponent={isTailwindComponent}
          stripeErr={stripeErr}
          validateStripeErr={validateStripeErr}
          onInlineCardPay={handlePayClick}
          inlinePayLoading={paymentLoader}
          isServiceProvider={isServiceProvider}
          isAdminCreateUser={isAdminCreateUser}
          isAdmin={isAdmin}
          from={from}
          isUpgradeCategory={isUpgradeCategory}
          isEnhancedToCompleteProtection={isEnhancedToCompleteProtection}
          finalAmount={finalAmount}
          onFinalAmountChange={onFinalAmountChange}
        />
      )}
    </>
  );

  const paymentAlerts = (
    <>
      {alertMsg && valdiateModal && (
        <>
          {Object.keys(propertyPayment).length > 0 &&
          propertyPayment?.success ? (
            <AlertDialog
              isTailwindComponent={isTailwindComponent}
              type="Success"
              openPopup={alertMsg}
              onClose={true}
              onCloseFun={() => {
                setAlertMsg(false);
                dispatch(propertiesAction.addNewProperty({}));
                localStorage.removeItem("promoCode");
                localStorage.removeItem("referralCode");
                onCloseModalHandler("success");
              }}
              mesage={successMsg}
            />
          ) : (
            <AlertDialog
              isTailwindComponent={isTailwindComponent}
              type="Error"
              openPopup={alertMsg}
              onClose={true}
              onCloseFun={() => {
                if (propertyPayment?.data?.message?.includes("Card expired")) {
                  setAlertMsg(false);
                  dispatch(propertiesAction.fethPayment({}));
                } else {
                  setAlertMsg(false);
                  dispatch(propertiesAction.clearPropertiesList("error"));
                  dispatch(userSigupAction.fetchTotalPaymentDetails({}));
                  if (!from && !isPaymentDone) {
                    // onChangeModalScreen("security"); //commneted for keep the payment modal on
                  }
                }
                onCloseModalHandler("error");
              }}
              mesage={errMsg}
            />
          )}
        </>
      )}
      <Loader open={paymentLoader || cardLoader || addNewCardLoader} />
    </>
  );

  if (isTailwindComponent) {
    return (
      <div className="tailwind_component">
        <div className="bg-card rounded-2xl border border-border shadow-lg overflow-x-hidden">
          {!hideSecureHeader && (
            <div className="bg-gradient-to-r from-secondary to-cyan-400 py-3 px-4 flex items-center justify-center gap-2 relative">
              <Lock className="h-4 w-4 text-white shrink-0" />
              <span className="text-white font-medium text-sm">
                Secure connection
              </span>
              <button
                type="button"
                onClick={handleCloseAddPropertyModal}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/90 hover:text-white p-1"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}

          <div className="p-3 sm:p-5 md:p-6">
            {paymentPanel}

            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-border">
              <TailwindButton
                type="button"
                variant="outline"
                onClick={goback}
                disabled={paymentLoader}
                className="rounded-full px-8"
              >
                Back
              </TailwindButton>

              <TailwindButton
                type="button"
                onClick={handlePayClick}
                disabled={isPayDisabled}
                className={TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS}
              >
                PAY SECURELY
              </TailwindButton>
            </div>
          </div>
        </div>
        {paymentAlerts}
      </div>
    );
  }

  return (
    <Box>
      <Typography
        align="center"
        variant="subtitle1"
        className="py-1 formHeader zoom-container"
        sx={{ position: "relative" }}
      >
        <LockIcon sx={{ fontSize: 16 }} className="me-1" />
        Secure connection
        <Button
          onClick={handleCloseAddPropertyModal}
          className="modal-close-btn close-text hide-inner-close"
          style={{
            position: "absolute",
            top: 0,
            right: -16,
            background: "transparent",
            textTransform: "none",
          }}
        >
          <CloseIcon
            className="hide-inner-close"
            sx={{ fontSize: 20, color: "white" }}
          />
        </Button>
      </Typography>
      {paymentPanel}

      <div className="d-flex align-items-center buttons-content-spacing justify-content-between mt-3 ">
        <Button
          onClick={goback}
          disabled={paymentLoader}
          className="popup_Btn other_popup_btn"
        >
          Back
        </Button>

        <Button
          type="submit"
          onClick={handlePayClick}
          className="popup_Btn success_popup_btn"
          variant="contained"
          disabled={isPayDisabled}
        >
          PAY SECURELY
        </Button>
      </div>
      {paymentAlerts}
    </Box>
  );
};

export default CardPayment;
